/**
 * @file useYocoCheckout.hook.ts
 * @path apps/website/src/features/booking/hooks/useYocoCheckout.hook.ts
 * @description
 * Handles Yoco payment initiation for the Website booking wizard.
 * Calls initWebsitePayment on the server, which stores a pending intent
 * and returns a Yoco Payment Link URL.
 */

import { useState } from "react";
import { toast } from "sonner";
import { httpsCallable } from "firebase/functions";
import { v4 as uuidv4 } from "uuid";
import functions from "@/config/firebase.config";

import { Activity, CompletedBooking, Service } from "@sirbastians/types";
import { useBookingStore } from "../booking.store";
import { calculateTotalForAllBookings } from "../pricing.util";

// ---------------------------------------------------------------------------
// Sanitisation types — strip non-serialisable / large fields before Firebase
// ---------------------------------------------------------------------------

type SanitizedActivity = Omit<Activity, "iconName">;

type SanitizedService = Omit<
  Service,
  "gallery" | "relatedActivities" | "includedActivities" | "optionalActivities"
> & {
  relatedActivities: SanitizedActivity[];
  includedActivities: SanitizedActivity[];
  optionalActivities: SanitizedActivity[];
};

type CompletedBookingPayload = Omit<
  CompletedBooking,
  | "bookingDate"
  | "selectedService"
  | "includedActivities"
  | "selectedActivities"
> & {
  bookingDate: string;
  selectedService: SanitizedService;
  includedActivities: SanitizedActivity[];
  selectedActivities: SanitizedActivity[];
};

type InitPaymentRequest = {
  bookings: CompletedBookingPayload[];
  amount: number;
};

type InitPaymentResponse = {
  paymentUrl: string;
  bookingReference: string;
  paymentReference: string;
  amount: number;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const sanitizeActivity = (activity: Activity): SanitizedActivity => {
  const { iconName, ...rest } = activity as Activity & { iconName?: unknown };
  return rest as SanitizedActivity;
};

const sanitizeService = (service: Service): SanitizedService => {
  const {
    gallery,
    relatedActivities,
    includedActivities,
    optionalActivities,
    ...rest
  } = service as Service & { gallery?: unknown };

  return {
    ...rest,
    relatedActivities: (relatedActivities ?? []).map(sanitizeActivity),
    includedActivities: (includedActivities ?? []).map(sanitizeActivity),
    optionalActivities: (optionalActivities ?? []).map(sanitizeActivity),
  };
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useYocoCheckout = () => {
  const store = useBookingStore();
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePayNow = async () => {
    setIsProcessing(true);

    try {
      const {
        selectedService,
        selectedDate,
        selectedTime,
        passengers,
        completedBookings,
        pricing,
        editingBookingId,
        includedActivities,
        selectedActivities,
        luggageCount,
        babySeats,
        trailerNeeded,
        flightNumber,
        duration,
        selectedVehicles,
        routeStops,
        adultsCount,
        childrenCount,
        infantsCount,
        saveCurrentBooking,
      } = store;

      const hasDraft =
        selectedService &&
        selectedDate &&
        selectedTime &&
        passengers.length > 0;

      const bookingsToSend: CompletedBooking[] = [...completedBookings];

      if (hasDraft) {
        const draftBooking: CompletedBooking = {
          id: editingBookingId || uuidv4(),
          bookingDate: selectedDate!,
          selectedService: selectedService!,
          includedActivities,
          selectedActivities,
          passengers,
          adultCount: adultsCount,
          childCount: childrenCount,
          infantCount: infantsCount,
          luggageCount,
          babySeats,
          trailerNeeded,
          flightNumber,
          selectedTime: selectedTime!,
          duration,
          selectedVehicles,
          routeStops,
          pricing: {
            service: pricing.servicePrice,
            activities: pricing.activitiesPrice,
            addOns:
              pricing.addOnsPrice +
              pricing.vehiclesPrice +
              pricing.extraStopsPrice,
            total: pricing.total,
          },
        };

        saveCurrentBooking();
        bookingsToSend.push(draftBooking);
      }

      if (bookingsToSend.length === 0) {
        toast.error("Please complete at least one booking before paying.");
        setIsProcessing(false);
        return;
      }

      const combinedTotal = calculateTotalForAllBookings(bookingsToSend);

      const payload: CompletedBookingPayload[] = bookingsToSend.map((b) => ({
        ...b,
        bookingDate: b.bookingDate.toISOString(),
        selectedService: sanitizeService(b.selectedService),
        includedActivities: b.includedActivities.map(sanitizeActivity),
        selectedActivities: b.selectedActivities.map(sanitizeActivity),
      }));

      // Sanity-check serialisation before sending
      try {
        JSON.stringify(payload);
      } catch (serErr) {
        console.error("Serialisation failed:", serErr);
        toast.error("Technical error: payment data could not be processed.");
        setIsProcessing(false);
        return;
      }

      // ── Call initWebsitePayment ─────────────────────────────────────────
      const initWebsitePayment = httpsCallable(functions, "initWebsitePayment");
      const result = await initWebsitePayment({
        bookings: payload,
        amount: combinedTotal,
      });
      const { paymentUrl, bookingReference, paymentReference } =
        result.data as InitPaymentResponse;

      const primaryPassenger =
        bookingsToSend[0].passengers.find((p) => p.isPrimary) ??
        bookingsToSend[0].passengers[0];

      // Save to localStorage for BookingConfirmation.page.tsx to read
      localStorage.setItem(
        `booking_${bookingReference}`,
        JSON.stringify({
          bookings: payload,
          primaryPassenger,
          paymentReference,
          bookingReference,
          timestamp: Date.now(),
        }),
      );

      window.location.href = paymentUrl;
    } catch (error) {
      console.error("Payment initialisation error:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Payment initialisation failed.";
      toast.error(
        message.includes("internal")
          ? "Server error. Please try again later."
          : message,
      );
      setIsProcessing(false);
    }
  };

  return { handlePayNow, isProcessing };
};
